// src/pages/Cleaning/Swaps/EditCleaningSwapListing.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../../config";



const STATES = ["VIC", "NSW", "QLD", "SA", "WA", "NT", "TAS", "ACT"];

const DEFAULT_SERVICES = {
  dusting: true,
  vacuuming: true,
  mopping: true,
  surfacesWiped: true,
  tidying: true,
  kitchenCleaning: true,
  bathroomCleaning: true,
  bedMaking: false,
  linenChanging: false,
  washing: false,
  ironing: false,
  pantryTidying: false,
  loungeVacuum: true,
  cobwebRemoval: false,
  lightSpotCleaning: true,
  dishwashing: false,
  organisation: false,
};

const Input = ({ label, value, set, type = "text", required = false }) => (
  <>
    <label style={labelBold}>{label}</label>
    <input
      style={input}
      type={type}
      required={required}
      value={value}
      onChange={(e) => set(e.target.value)}
    />
  </>
);

const Checkbox = ({ label, value, setValue }) => (
  <label style={checkboxRow}>
    <input
      type="checkbox"
      checked={!!value}
      onChange={(e) => setValue(e.target.checked)}
    />
    {label}
  </label>
);

function safeServices(raw) {
  if (!raw) return { ...DEFAULT_SERVICES };
  if (typeof raw === "object") return { ...DEFAULT_SERVICES, ...raw };

  // sometimes DB sends JSON string
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object")
        return { ...DEFAULT_SERVICES, ...parsed };
    } catch (e) {
      // ignore
    }
  }

  return { ...DEFAULT_SERVICES };
}

export default function EditCleaningSwapListing() {
    const normalizeCleaningTime = (val) => {
  if (val === null || val === undefined || val === "") return "";

  const num = Number(val);
  if (Number.isNaN(num)) return "";

  const allowed = ["1","1.5","2","2.5","3","3.5","4","4.5","5"];
  const str = String(num);

  return allowed.includes(str) ? str : "";
};

  const { listingId } = useParams(); // must match your App.js route param
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  // ====== FORM STATE (match post form structure) ======
  const [avgJobPrice, setAvgJobPrice] = useState("");
  const [swapFromSuburb, setSwapFromSuburb] = useState("");
  const [swapFromPostcode, setSwapFromPostcode] = useState("");
  const [swapFromState, setSwapFromState] = useState("");

  const [swapToSuburb, setSwapToSuburb] = useState("");
  const [swapToPostcode, setSwapToPostcode] = useState("");
  const [swapToState, setSwapToState] = useState("");

  const [frequency, setFrequency] = useState("");
  const [cleaningTime, setCleaningTime] = useState("");

  const [accessType, setAccessType] = useState("");
  const [streetParking, setStreetParking] = useState(false);
  const [petsAtHome, setPetsAtHome] = useState(false);
  const [alarmCodeRequired, setAlarmCodeRequired] = useState(false);

  const [bedroomsCleaned, setBedroomsCleaned] = useState(1);
  const [bathroomsCleaned, setBathroomsCleaned] = useState(1);
  const [toiletsCleaned, setToiletsCleaned] = useState(1);

  const [kitchenIncluded, setKitchenIncluded] = useState(true);
  const [livingAreasIncluded, setLivingAreasIncluded] = useState(true);

  // You had these in earlier payloads; keep them so backend stays happy
  const [homeSize, setHomeSize] = useState(""); // your post form doesn’t show it, but payload includes it
  const [serviceDay, setServiceDay] = useState("any");
  const [serviceTime, setServiceTime] = useState("any");

  const [services, setServices] = useState({ ...DEFAULT_SERVICES });

  const toggleService = (key) => {
    setServices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const serviceKeys = useMemo(() => Object.keys(DEFAULT_SERVICES), []);

  // ====== LOAD EXISTING LISTING ======
  useEffect(() => {
    async function loadSwap() {
      try {
        setLoading(true);
        setError("");
        setMsg("");

        const token = localStorage.getItem("token");

        const res = await fetch(
          `${API_BASE_URL}/api/cleaning/swap-listings/${listingId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();
        if (!res.ok) {
          setError(data?.error || "Failed to load swap listing");
          return;
        }

        const s = data.listing || data.swap || data.data || data;

       setAvgJobPrice(s.avg_job_price ?? "");
setSwapFromSuburb(s.swap_from_suburb ?? "");
setSwapFromPostcode(s.swap_from_postcode ?? "");
setSwapFromState(s.swap_from_state ?? "");

setSwapToSuburb(s.swap_to_suburb ?? "");
setSwapToPostcode(s.swap_to_postcode ?? "");
setSwapToState(s.swap_to_state ?? "");

setFrequency(
  s.frequency !== null && s.frequency !== undefined
    ? String(s.frequency)
    : ""
);

setCleaningTime(
  s.cleaningTime !== null && s.cleaningTime !== undefined
    ? String(Number(s.cleaningTime))
    : ""
);



setAccessType(s.accessType ?? "");
setStreetParking(!!s.streetParking);
setPetsAtHome(!!s.petsAtHome);
setAlarmCodeRequired(!!s.alarmCodeRequired);

setBedroomsCleaned(s.bedroomsCleaned ?? 1);
setBathroomsCleaned(s.bathroomsCleaned ?? 1);
setToiletsCleaned(s.toiletsCleaned ?? 1);

setKitchenIncluded(s.kitchenIncluded === 1 || s.kitchenIncluded === true);
setLivingAreasIncluded(
  s.livingAreasIncluded === 1 || s.livingAreasIncluded === true
);

setHomeSize(s.homeSize ?? "");
setServiceDay(s.serviceDay ?? "");
setServiceTime(s.serviceTime ?? "");

setServices(safeServices(s.services));

      } catch (e) {
        console.error("EDIT SWAP LOAD ERROR:", e);
        setError("Failed to load swap listing");
      } finally {
        setLoading(false);
      }
    }

    if (listingId) loadSwap();
    else {
      setLoading(false);
      setError("Missing listingId in URL");
    }
  }, [listingId]);

  // ====== SUBMIT UPDATE ======
  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setMsg("");
      setError("");

      const token = localStorage.getItem("token");

      const payload = {
        avg_job_price: Number(avgJobPrice),
        swap_from_suburb: swapFromSuburb,
        swap_from_postcode: swapFromPostcode,
        swap_from_state: swapFromState,
        swap_to_suburb: swapToSuburb,
        swap_to_postcode: swapToPostcode,
        swap_to_state: swapToState,

        frequency,
        cleaningTime: Number(cleaningTime),

        accessType,
        streetParking: streetParking ? 1 : 0,
        petsAtHome: petsAtHome ? 1 : 0,
        alarmCodeRequired: alarmCodeRequired ? 1 : 0,

        homeSize,
        bedroomsCleaned: Number(bedroomsCleaned),
        bathroomsCleaned: Number(bathroomsCleaned),
        toiletsCleaned: Number(toiletsCleaned),

        kitchenIncluded: kitchenIncluded ? 1 : 0,
        livingAreasIncluded: livingAreasIncluded ? 1 : 0,

        serviceDay,
        serviceTime,

        services,
      };

      const res = await fetch(
        `${API_BASE_URL}/api/cleaning/swap-listings/${listingId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const out = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("EDIT SWAP SAVE ERROR:", out);
        setError(out?.error || "Failed to save changes");
        return;
      }

      setMsg("✅ Changes saved!");
      // go back to details
      navigate(`/cleaning/swaps/${listingId}`);
    } catch (err) {
      console.error("EDIT SWAP SAVE ERROR:", err);
      setError("Failed to save changes");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p style={{ padding: 20 }}>Loading…</p>;
  if (error) return <p style={{ padding: 20, color: "red" }}>{error}</p>;

  return (
    <div style={container}>
      <h2 style={pageHeader}>✏️ Edit Cleaning Swap Listing</h2>

      {msg && <div style={{ ...messageBox, color: "#2e7d32" }}>{msg}</div>}

      <form style={form} onSubmit={handleSubmit}>
        {/* PRICING */}
        <h3 style={sectionHeader}>Swap Value</h3>
       <label style={labelBold}>Average Job Price ($)</label>
<input
  style={input}
  type="number"
  required
  placeholder="e.g. 85"
  value={avgJobPrice}
  onChange={(e) => setAvgJobPrice(e.target.value)}
/>


        {/* SWAP LOCATIONS */}
        <h3 style={sectionHeader}>Swap Locations</h3>

        <label style={labelBold}>Current Suburb</label>
        <input
          style={input}
          required
          value={swapFromSuburb}
          onChange={(e) => setSwapFromSuburb(e.target.value)}
        />

        <label style={labelBold}>Current Postcode</label>
        <input
          style={input}
          required
          value={swapFromPostcode}
          onChange={(e) => setSwapFromPostcode(e.target.value)}
        />

        <label style={labelBold}>Current State</label>
        <select
          style={input}
          required
          value={swapFromState}
          onChange={(e) => setSwapFromState(e.target.value)}
        >
          <option value="">Select</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <label style={labelBold}>Preferred Suburb</label>
        <input
          style={input}
          required
          value={swapToSuburb}
          onChange={(e) => setSwapToSuburb(e.target.value)}
        />

        <label style={labelBold}>Preferred Postcode</label>
        <input
          style={input}
          required
          value={swapToPostcode}
          onChange={(e) => setSwapToPostcode(e.target.value)}
        />

        <label style={labelBold}>Preferred State</label>
        <select
          style={input}
          required
          value={swapToState}
          onChange={(e) => setSwapToState(e.target.value)}
        >
          <option value="">Select</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        {/* JOB DETAILS */}
        <h3 style={sectionHeader}>Job Details</h3>

        <label style={labelBold}>Cleaning Time</label>
        <select
          style={input}
          required
          value={cleaningTime}
          onChange={(e) => setCleaningTime(e.target.value)}
        >
          <option value="">Select</option>
          <option value="1">1 hr</option>
          <option value="1.5">1.5 hr</option>
          <option value="2">2 hr</option>
          <option value="2.5">2.5 hr</option>
          <option value="3">3 hr</option>
          <option value="3.5">3.5 hr</option>
          <option value="4">4 hr</option>
          <option value="4.5">4.5 hr</option>
          <option value="5">5 hr</option>
        </select>

        <label style={labelBold}>Frequency</label>
        <select
          style={input}
          required
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
        >
          <option value="weekly">Weekly</option>
          <option value="fortnightly">Fortnightly</option>
          <option value="monthly">Monthly</option>
        </select>
{/* SERVICE SCHEDULE */}
<label style={labelBold}>Preferred Day</label>
<select
  style={input}
  value={serviceDay}
  onChange={(e) => setServiceDay(e.target.value)}
>
  <option value="any">Any Day</option>
  <option value="monday">Monday</option>
  <option value="tuesday">Tuesday</option>
  <option value="wednesday">Wednesday</option>
  <option value="thursday">Thursday</option>
  <option value="friday">Friday</option>
  <option value="saturday">Saturday</option>
  <option value="sunday">Sunday</option>
</select>

<label style={labelBold}>Preferred Time</label>
<select
  style={input}
  value={serviceTime}
  onChange={(e) => setServiceTime(e.target.value)}
>
  <option value="any">Any Time</option>
  <option value="morning">Morning</option>
  <option value="midday">Midday</option>
  <option value="afternoon">Afternoon</option>
</select>
        {/* CLIENT OVERVIEW */}
        <h3 style={sectionHeader}>Client Overview</h3>

        <label style={labelBold}>Access Type</label>
        <select
          style={input}
          value={accessType}
          onChange={(e) => setAccessType(e.target.value)}
        >
          <option>Access Available Anytime</option>
          <option>Key Safe</option>
          <option>Owner Home</option>
          <option>Tenant Home</option>
        </select>

        <Checkbox
          label="Street Parking"
          value={streetParking}
          setValue={setStreetParking}
        />
        <Checkbox label="Pets at Home" value={petsAtHome} setValue={setPetsAtHome} />
        <Checkbox
          label="Alarm Code Required"
          value={alarmCodeRequired}
          setValue={setAlarmCodeRequired}
        />

        {/* PROPERTY SIZE */}
        <h3 style={sectionHeader}>Property Size</h3>

        <Input
          label="Bedrooms"
          value={bedroomsCleaned}
          set={setBedroomsCleaned}
          type="number"
        />
        <Input
          label="Bathrooms"
          value={bathroomsCleaned}
          set={setBathroomsCleaned}
          type="number"
        />
        <Input
          label="Toilets"
          value={toiletsCleaned}
          set={setToiletsCleaned}
          type="number"
        />

        <Checkbox
          label="Kitchen Included"
          value={kitchenIncluded}
          setValue={setKitchenIncluded}
        />
        <Checkbox
          label="Living Areas Included"
          value={livingAreasIncluded}
          setValue={setLivingAreasIncluded}
        />

        {/* ROUTINE TASKS */}
        <h3 style={sectionHeader}>Routine Cleaning Tasks</h3>
        {serviceKeys.map((k) => (
          <Checkbox
            key={k}
            label={k.replace(/([A-Z])/g, " $1")}
            value={services[k]}
            setValue={() => toggleService(k)}
          />
        ))}

        <button type="submit" style={button} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>

        <button
          type="button"
          style={buttonSecondary}
          onClick={() => navigate(`/cleaning/swaps/${listingId}`)}
        >
          Cancel
        </button>
      </form>
    </div>
  );
}

/* ================= STYLES (match post vibe) ================= */
const container = {
  maxWidth: 650,
  margin: "40px auto",
  padding: 24,
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
};

const pageHeader = {
  textAlign: "center",
  marginBottom: 25,
  color: "#2e7d32",
  fontWeight: "bold",
};

const messageBox = {
  marginBottom: 20,
  textAlign: "center",
  fontWeight: 600,
};

const form = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const input = {
  padding: 10,
  borderRadius: 6,
  border: "1px solid #ccc",
  width: "100%",
};

const labelBold = { fontWeight: "bold" };

const checkboxRow = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontWeight: 500,
};

const sectionHeader = {
  borderBottom: "2px solid #4CAF50",
  paddingBottom: 6,
  marginTop: 10,
};

const button = {
  padding: 14,
  background: "#2e7d32",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  fontWeight: "bold",
  cursor: "pointer",
  marginTop: 10,
};

const buttonSecondary = {
  padding: 14,
  background: "#e0e0e0",
  color: "#111",
  border: "none",
  borderRadius: 8,
  fontWeight: "bold",
  cursor: "pointer",
};
